import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { publicService } from "../services/publicService";
import {
	getSelectedResourceIds,
	upsertSelectedResource,
	removeSelectedResource,
} from "../utils/inquirySelection";

const LEVEL_LABELS = {
	Expert: "Expert",
	Intermediate: "Intermediate",
	Beginner: "Beginner",
};

const LEVEL_COLORS = {
	Expert: "bg-green-500",
	Intermediate: "bg-yellow-400",
	Beginner: "bg-orange-400",
};

const LEVEL_TEXT = {
	Expert: "text-green-700 bg-green-50 border-green-200",
	Intermediate: "text-yellow-700 bg-yellow-50 border-yellow-200",
	Beginner: "text-orange-700 bg-orange-50 border-orange-200",
};

export default function ResourceDetail() {
	const { id } = useParams();
	const navigate = useNavigate();
	const [resource, setResource] = useState(null);
	const [loading, setLoading] = useState(true);
	const [selectedResourceIds, setSelectedResourceIds] = useState([]);
	const [commentModalOpen, setCommentModalOpen] = useState(false);
	const [selectionComment, setSelectionComment] = useState("");

	useEffect(() => {
		setSelectedResourceIds(getSelectedResourceIds());
	}, []);

	useEffect(() => {
		setLoading(true);
		publicService
			.getResourceById(id)
			.then((data) => {
				setResource(data || null);
			})
			.catch(() => {
				setResource(null);
			})
			.finally(() => setLoading(false));
	}, [id]);

	if (loading) {
		return (
			<div className="min-h-screen flex flex-col bg-gray-50">
				<Navbar />
				<main className="flex-1 flex items-center justify-center min-h-64 py-24">
					<div className="w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
				</main>
				<Footer />
			</div>
		);
	}

	if (!resource) {
		return (
			<div className="min-h-screen flex flex-col bg-gray-50">
				<Navbar />
				<main className="flex-1 max-w-7xl mx-auto px-4 py-24 text-center">
					<div className="text-6xl mb-4">🔍</div>
					<h2 className="text-2xl font-bold text-gray-900 mb-4">
						Resource Not Found
					</h2>
					<p className="text-gray-600 mb-8">
						Resource ID-{id} does not exist in our database.
					</p>
					<Link to="/" className="btn-primary">
						← Back to Home
					</Link>
				</main>
				<Footer />
			</div>
		);
	}

	const { role, experience, skills, categories, projects } = resource;
	const numericId = Number(id);
	const isSelected = selectedResourceIds.includes(numericId);
	const skillEntries = Object.entries(skills || {}).sort(([, a], [, b]) => {
		const rank = { Expert: 3, Intermediate: 2, Beginner: 1 };
		return (rank[b] || 0) - (rank[a] || 0);
	});

	const expertSkills = skillEntries.filter(([, value]) => value === "Expert");
	const intermediateSkills = skillEntries.filter(
		([, value]) => value === "Intermediate",
	);
	const beginnerSkills = skillEntries.filter(
		([, value]) => value === "Beginner",
	);

	const SENIOR_THRESHOLD = 8;
	const MID_THRESHOLD = 4;

	const expNum = Number(experience) || 0;
	const level =
		expNum >= SENIOR_THRESHOLD
			? "Senior"
			: expNum >= MID_THRESHOLD
				? "Mid-level"
				: "Junior";

	const handleToggleSelect = () => {
		if (isSelected) {
			const next = removeSelectedResource(numericId).map((item) => item.id);
			setSelectedResourceIds(next);
			return;
		}

		setCommentModalOpen(true);
	};

	const handleConfirmSelection = () => {
		const comment = selectionComment.trim();
		if (!comment) {
			return;
		}

		const next = upsertSelectedResource(numericId, comment).map(
			(item) => item.id,
		);
		setSelectedResourceIds(next);
		setCommentModalOpen(false);
		setSelectionComment("");
	};

	return (
		<div className="min-h-screen flex flex-col bg-gray-50">
			<Navbar />
			<main className="flex-1 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
				<button
					onClick={() => navigate(-1)}
					className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6 transition-colors group"
				>
					<svg
						className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform"
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M15 19l-7-7 7-7"
						/>
					</svg>
					Back to Directory
				</button>

				<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
					<div className="space-y-5">
						<div className="card p-6 text-center">
							<div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 text-white flex items-center justify-center font-bold text-2xl mx-auto mb-4">
								{String(id).padStart(3, "0")}
							</div>
							<div className="text-xs text-gray-400 font-medium mb-1">
								Resource ID-{id}
							</div>
							<h1 className="text-lg font-bold text-gray-900 mb-1">{role}</h1>
							<div className="text-sm text-gray-500 mb-4">
								{level} Professional
							</div>
							<div className="grid grid-cols-2 gap-3 text-center">
								<div className="bg-primary-50 rounded-lg p-3">
									<div className="text-2xl font-bold text-primary-700">
										{expNum}+
									</div>
									<div className="text-xs text-gray-500 mt-0.5">Years Exp.</div>
								</div>
								<div className="bg-green-50 rounded-lg p-3">
									<div className="text-2xl font-bold text-green-700">
										{expertSkills.length}
									</div>
									<div className="text-xs text-gray-500 mt-0.5">
										Expert Skills
									</div>
								</div>
							</div>
							<button
								type="button"
								onClick={handleToggleSelect}
								className={`w-full mt-4 py-2 rounded-lg text-sm font-medium transition-colors border ${
									isSelected
										? "bg-primary-600 text-white border-primary-600 hover:bg-primary-700"
										: "text-primary-700 border-primary-200 hover:bg-primary-50"
								}`}
							>
								{isSelected ? "✓" : "+"}
							</button>
						</div>

						<div className="card p-5">
							<h3 className="font-semibold text-gray-900 mb-4">
								Technology Stack by Category
							</h3>
							<div className="space-y-4">
								{(categories || []).map((cat) => {
									const categoryName = cat?.name || "Unknown Category";
									const competency = cat?.competency;
									const categorySkills = cat?.skills || [];

									return (
										<div
											key={cat?.id || categoryName}
											className="border-l-4 border-primary-500 pl-4 py-2"
										>
											{/* Category Header */}
											<div className="flex items-center gap-2 mb-3">
												<span className="badge bg-primary-50 text-primary-700 border border-primary-100 font-semibold">
													{categoryName}
												</span>
												{competency && (
													<span
														className={`text-xs font-medium px-2 py-0.5 rounded-md border ${LEVEL_TEXT[competency] || ""}`}
													>
														{competency}
													</span>
												)}
											</div>

											{/* Category Skills */}
											<div className="flex flex-wrap gap-2">
												{categorySkills.length > 0 ? (
													categorySkills.map((skill) => {
														const skillName = skill?.name || "Unknown Skill";
														const skillCompetency = skill?.competency;
														return (
															<span
																key={skill?.id || skillName}
																className={`text-xs font-medium px-3 py-1.5 rounded-md border ${
																	LEVEL_TEXT[skillCompetency] ||
																	"text-gray-500 bg-gray-50 border-gray-200"
																}`}
															>
																{skillName}
																<span className="ml-1 font-bold">•</span>
																<span className="ml-1">
																	{skillCompetency || "Unknown"}
																</span>
															</span>
														);
													})
												) : (
													<span className="text-xs text-gray-500 italic">
														No skills assigned
													</span>
												)}
											</div>
										</div>
									);
								})}
							</div>
						</div>
					</div>

					<div className="lg:col-span-2 space-y-6">
						<div className="card p-6">
							<h2 className="text-lg font-bold text-gray-900 mb-4">
								Skill Proficiency Overview
							</h2>
							<div className="grid grid-cols-3 gap-4 mb-6">
								{[
									{
										label: "Expert",
										count: expertSkills.length,
										color: "text-green-700",
										bg: "bg-green-50 border-green-200",
									},
									{
										label: "Intermediate",
										count: intermediateSkills.length,
										color: "text-yellow-700",
										bg: "bg-yellow-50 border-yellow-200",
									},
									{
										label: "Beginner",
										count: beginnerSkills.length,
										color: "text-orange-700",
										bg: "bg-orange-50 border-orange-200",
									},
								].map((skillGroup) => (
									<div
										key={skillGroup.label}
										className={`${skillGroup.bg} border rounded-lg p-3 text-center`}
									>
										<div className={`text-2xl font-bold ${skillGroup.color}`}>
											{skillGroup.count}
										</div>
										<div className="text-xs text-gray-600 mt-0.5">
											{skillGroup.label}
										</div>
									</div>
								))}
							</div>

							<div className="space-y-3">
								{skillEntries.map(([skill, value]) => {
									const widthPercent =
										value === "Expert"
											? 100
											: value === "Intermediate"
												? 66
												: 33;

									return (
										<div key={skill} className="flex items-center gap-3">
											<div className="w-36 text-sm text-gray-700 font-medium truncate flex-shrink-0">
												{skill}
											</div>
											<div className="flex-1 bg-gray-100 rounded-full h-2">
												<div
													className={`h-2 rounded-full ${LEVEL_COLORS[value] || "bg-gray-300"} transition-all duration-500`}
													style={{ width: `${widthPercent}%` }}
												></div>
											</div>
											<span
												className={`badge border text-xs ${LEVEL_TEXT[value] || "text-gray-500 bg-gray-50 border-gray-200"} w-24 justify-center flex-shrink-0`}
											>
												{LEVEL_LABELS[value] || "Unknown"}
											</span>
										</div>
									);
								})}
							</div>
						</div>

						{expertSkills.length > 0 && (
							<div className="card p-6">
								<h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
									<span className="w-3 h-3 bg-green-500 rounded-full"></span>{" "}
									Expert Skills
								</h3>
								<div className="flex flex-wrap gap-2">
									{expertSkills.map(([skill]) => (
										<span key={skill} className="badge skill-expert">
											{skill}
										</span>
									))}
								</div>
							</div>
						)}

						{intermediateSkills.length > 0 && (
							<div className="card p-6">
								<h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
									<span className="w-3 h-3 bg-yellow-400 rounded-full"></span>{" "}
									Intermediate Skills
								</h3>
								<div className="flex flex-wrap gap-2">
									{intermediateSkills.map(([skill]) => (
										<span key={skill} className="badge skill-intermediate">
											{skill}
										</span>
									))}
								</div>
							</div>
						)}

						{beginnerSkills.length > 0 && (
							<div className="card p-6">
								<h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
									<span className="w-3 h-3 bg-orange-400 rounded-full"></span>{" "}
									Beginner Skills
								</h3>
								<div className="flex flex-wrap gap-2">
									{beginnerSkills.map(([skill]) => (
										<span key={skill} className="badge skill-beginner">
											{skill}
										</span>
									))}
								</div>
							</div>
						)}

						{(projects || []).length > 0 && (
							<div className="card p-6">
								<h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
									<svg
										className="w-5 h-5 text-primary-600"
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
										/>
									</svg>
									Project Experience
								</h2>
								<div className="space-y-4">
									{projects.map((project, index) => (
										<div
											key={index}
											className="bg-gray-50 border border-gray-200 rounded-lg p-5 hover:border-primary-300 transition-colors"
										>
											<div className="flex items-start justify-between mb-3">
												<div>
													<h4 className="font-bold text-gray-900 text-base">
														{project.project_name}
													</h4>
													<p className="text-sm text-primary-600 font-medium">
														Role: {project.role}
													</p>
												</div>
											</div>

											<div className="mb-4">
												<p className="text-sm text-gray-700 mb-3">
													<span className="font-semibold text-gray-900">
														Project Summary:
													</span>
												</p>
												<p className="text-sm text-gray-600 leading-relaxed">
													{project.project_summary}
												</p>
											</div>

											<div className="mb-4">
												<p className="text-sm text-gray-700 mb-3">
													<span className="font-semibold text-gray-900">
														Key Responsibilities:
													</span>
												</p>
												<p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">
													{project.main_responsibilities}
												</p>
											</div>

											<div>
												<p className="text-xs text-gray-500 font-semibold mb-2">
													TECHNOLOGIES & TOOLS
												</p>
												<div className="flex flex-wrap gap-2">
													{(project.tools_technologies || "")
														.split(/[,;]/)
														.map((tool, i) => (
															<span
																key={i}
																className="badge bg-blue-50 text-blue-700 border border-blue-200 text-xs"
															>
																{tool.trim()}
															</span>
														))}
												</div>
											</div>
										</div>
									))}
								</div>
							</div>
						)}
					</div>
				</div>
			</main>
			<Footer />
			{commentModalOpen && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
					<div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6">
						<h3 className="text-lg font-semibold text-gray-900 mb-2">
							Add Comment for Inquiry
						</h3>
						<p className="text-sm text-gray-600 mb-4">Resource ID-{id}</p>
						<textarea
							value={selectionComment}
							onChange={(e) => setSelectionComment(e.target.value)}
							rows={4}
							className="input-field resize-none"
							placeholder="Enter mandatory comment for this resource"
						/>
						<div className="mt-5 flex justify-end gap-2">
							<button
								type="button"
								onClick={() => {
									setCommentModalOpen(false);
									setSelectionComment("");
								}}
								className="btn-secondary"
							>
								Cancel
							</button>
							<button
								type="button"
								onClick={handleConfirmSelection}
								disabled={!selectionComment.trim()}
								className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
							>
								Save
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
